import type {
  OrderFlagEntry,
  OrderFlagScope,
  OrderFlagStateEntry,
} from '../reducers/orderFlags';

export const ORDER_FLAGS_LOADED = 'ORDER_FLAGS_LOADED';
export const ORDER_FLAG_CHANGE_REQUESTED = 'ORDER_FLAG_CHANGE_REQUESTED';
export const ORDER_FLAG_CHANGE_SETTLED = 'ORDER_FLAG_CHANGE_SETTLED';
export const ORDER_FLAG_CHANGE_FAILED = 'ORDER_FLAG_CHANGE_FAILED';
export const ORDER_FLAGS_RESET = 'ORDER_FLAGS_RESET';
export const ORDER_FLAGS_SCOPE_CHECKED = 'ORDER_FLAGS_SCOPE_CHECKED';

/**
 * Carga completa desde el servidor para un dueño (`userId:tenantId`).
 *
 * `requestedAt` (ms) es cuándo se PIDIÓ: la foto del servidor no sabe nada de
 * lo confirmado después, así que esas órdenes conservan su valor actual. Si no
 * se da, se toma `loadedAt`.
 */
export function orderFlagsLoaded(
  ownerKey: string,
  entries: OrderFlagEntry[],
  loadedAt: number,
  requestedAt: number = loadedAt
) {
  return {
    type: ORDER_FLAGS_LOADED,
    payload: { ownerKey, entries, loadedAt, requestedAt },
  } as const;
}

/** Cambio optimista: queda como pendiente hasta que el servidor lo confirme. */
export function orderFlagChangeRequested(
  orderIds: string[],
  flagged: boolean,
  changedAt: string
) {
  return {
    type: ORDER_FLAG_CHANGE_REQUESTED,
    payload: { orderIds, flagged, changedAt },
  } as const;
}

/**
 * Respuesta del servidor (`OrderFlagState[]`). `settledAt` (ms, mismo reloj que
 * `requestedAt` de la carga) es cuándo llegó.
 */
export function orderFlagChangeSettled(
  states: OrderFlagStateEntry[],
  settledAt: number
) {
  return {
    type: ORDER_FLAG_CHANGE_SETTLED,
    payload: { states, settledAt },
  } as const;
}

/** El cambio falló: se revierte solo si sigue siendo el de ese `changedAt`. */
export function orderFlagChangeFailed(orderIds: string[], changedAt: string) {
  return {
    type: ORDER_FLAG_CHANGE_FAILED,
    payload: { orderIds, changedAt },
  } as const;
}

/** Vacía el estado y lo deja a nombre del dueño nuevo (puede ser `null`). */
export function orderFlagsReset(ownerKey: string | null) {
  return { type: ORDER_FLAGS_RESET, payload: { ownerKey } } as const;
}

/**
 * El servidor confirmó el ámbito (persona o grupo) y su sello. Se ignora si
 * `ownerKey` ya no es el del estado (el usuario cambió mientras volaba).
 */
export function orderFlagsScopeChecked(
  ownerKey: string,
  scope: OrderFlagScope,
  checkedAt: number
) {
  return {
    type: ORDER_FLAGS_SCOPE_CHECKED,
    payload: { ownerKey, scope, checkedAt },
  } as const;
}

export type OrderFlagsAction =
  | ReturnType<typeof orderFlagsLoaded>
  | ReturnType<typeof orderFlagChangeRequested>
  | ReturnType<typeof orderFlagChangeSettled>
  | ReturnType<typeof orderFlagChangeFailed>
  | ReturnType<typeof orderFlagsReset>
  | ReturnType<typeof orderFlagsScopeChecked>;
